import * as React from 'react'

import type { Assign } from '@ark-ui/react'
import {
  PasswordInput as ArkPasswordInput,
  usePasswordInputContext,
} from '@ark-ui/react/password-input'

import { Button } from '~/components/button'
import { cn } from '~/utils/cn'

import { Icon, type IconProps } from '~/components/icon'

import {
  InputPasswordTag,
  type InputPasswordTagProps,
} from './input-password-tag'

import {
  control as controlStyles,
  input as inputStyles,
} from './input-password.styles'

import type { InputPasswordSharedProps } from './input-password-root'

type InputPasswordInputProps = Assign<
  React.ComponentProps<typeof ArkPasswordInput.Input>,
  InputPasswordSharedProps
> & {
  // Usamos o nome "prefix" para deixar o componente pronto para futuras extensões a novos casos de uso.
  prefix?: IconProps['symbol']
  strengthRules: Record<NonNullable<InputPasswordTagProps['variant']>, RegExp>
}

export function InputPasswordInput({
  prefix,
  strengthRules,
  size = 'ml',
  onChange,
  readOnly,
  ...props
}: InputPasswordInputProps) {
  const {
    visible: isPasswordVisible,
    disabled,
    invalid,
  } = usePasswordInputContext()

  const [strengthLevel, setStrengthLevel] = React.useState<
    InputPasswordTagProps['variant'] | null
  >(null)

  const isInteractive = !disabled && !readOnly
  const shouldShowStrengthLevel = strengthLevel && isInteractive
  const shouldShowVisibilityTrigger = isInteractive

  function getColorPrefix(): IconProps['color'] {
    if (disabled) return 'colorTextNeutralDisabled'
    if (readOnly) return 'colorTextNeutralReadonly'

    return 'colorTextNeutralDefault'
  }

  const onStrengthLevel = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value

      for (const level of Object.keys(strengthRules)) {
        const _level = level as InputPasswordTagProps['variant']
        const rule = strengthRules[_level!]

        if (rule.test(value)) {
          setStrengthLevel(_level)
          break
        }
      }

      if (value.length === 0) {
        setStrengthLevel(null)
      }

      onChange?.(event)
    },
    []
  )

  return (
    <ArkPasswordInput.Control
      className={cn(
        controlStyles({
          size,
          isDisabled: disabled,
          isReadOnly: readOnly,
          isError: invalid,
          className: 'overflow-red-500',
        })
      )}
    >
      {prefix && (
        <Icon
          size={size === 'md' ? 'extra-small' : 'small'}
          color={getColorPrefix()}
          symbol={prefix}
        />
      )}

      <ArkPasswordInput.Input
        {...props}
        className={cn(
          inputStyles({
            size,
            isDisabled: disabled,
            isReadOnly: readOnly,
          })
        )}
        placeholder="Hidden placeholder"
        onChange={onStrengthLevel}
        readOnly={readOnly}
      />

      {shouldShowStrengthLevel && <InputPasswordTag variant={strengthLevel} />}

      {shouldShowVisibilityTrigger && (
        <ArkPasswordInput.VisibilityTrigger asChild>
          <Button.Root
            variant="neutral-tertiary"
            size={size === 'md' ? 'md' : 'ml'}
          >
            <Button.Icon
              symbol={isPasswordVisible ? 'rdicon-eye-off' : 'rdicon-eye'}
            />
          </Button.Root>
        </ArkPasswordInput.VisibilityTrigger>
      )}
    </ArkPasswordInput.Control>
  )
}

InputPasswordInput.displayName = 'InputPasswordInput'
